<template>
  <!--
    Aggregated profile card (new channel layout).

    Net-new card mounted at the TOP of the new channel page. It aggregates the
    four capability managers' status + XP + the "X/5 Quests Completed" counter +
    action buttons into a single card. URQL only — it derives ALL of its data
    from existing channel-store getters / the wallet capability service, so it
    adds NO new GraphQL query.

    This component is a thin orchestrator: it owns the data derivation and the
    auth gate / panel chrome, then delegates each of the four columns to a
    focused presentational child. ONE row of FOUR sibling columns separated by
    `gap-8`, matching the desktop logged-in profile-card design:
      1. IDENTITY (fixed `w-[135px]`, centered): brand-ring avatar + name +
         View Profile.
      2. TOTAL XP (flex-1): header, then [XP pill | Quests-progress pill], then
         a full-width "Invite friends … +reward" row pill.
      3. MEMBERSHIP/WALLET (flex-1): header, [membership pill | wallet pill],
         then the full-width "Upgrade / Top up" button.
      4. SUPERFAN+ (flex-1): header, [ACTIVE/Free pill], then the full-width
         "See Benefits" button.
    All pills + action buttons are WHITE pills (`bg-surface` + `border-default`)
    on the light-grey `bg-surface-secondary` card — theme-adaptive tokens only.

    The `completedLabel` prop / `#quests-counter` slot fill the quests counter;
    the `upgrade` / `see-benefits` events are routed by the host to the auth
    orchestrator / benefits surface.

    Auth gate: the PARENT is the primary gate (`v-if="userStore.isAuthenticated"`).
    This card additionally no-ops when unauthenticated so Upgrade/Top-up can
    never leak to logged-out users.
  -->
  <section
    v-if="userStore.isAuthenticated && userStore.user"
    :class="[
      'flex flex-col gap-6 lg:flex-row lg:items-stretch lg:gap-8',
      // `bare`: when the card is hosted inside the unified hero panel
      // (channel.vue groups it with the MEANWHILE row in ONE rounded panel) it
      // must NOT draw its own panel chrome — only the inner padding so the row
      // spacing matches the design. Default (standalone) keeps the full rounded-
      // border-surface panel so any other / legacy usage is unchanged.
      bare
        ? 'p-6 lg:p-8'
        : 'mx-5 rounded-[20px] border border-border bg-surface-secondary p-6',
    ]"
    data-testid="channel-profile-card"
  >
    <ChannelProfileIdentityColumn
      :username="userStore.username"
      :avatar="userStore.user.avatar"
      :user-id="userStore.user.id"
      :country-iso="userStore.user.country_iso"
      :is-superfan="isSuperfan"
      :channel-slug="channelStore.channelSlug"
    />

    <ChannelProfileXpColumn
      :formatted-xp="formattedXp"
      :completed-label="completedLabel"
      :quests-track-width="questsTrackWidth"
      :invite-friends-reward="INVITE_FRIENDS_REWARD"
      @invite="onInviteFriends"
    >
      <template v-if="$slots['quests-counter']" #quests-counter>
        <slot name="quests-counter" />
      </template>
    </ChannelProfileXpColumn>

    <ChannelProfileMembershipColumn
      :membership-label="membershipLabel"
      :has-paid-tier="hasPaidTier"
      :wallet-token-label="walletToken?.label ?? null"
      :formatted-token-balance="formattedTokenBalance"
      :has-any-upgrade-path="hasAnyUpgradePath"
      @upgrade="emit('upgrade')"
    />

    <ChannelProfileSuperfanColumn
      :is-superfan="isSuperfan"
      @see-benefits="emit('see-benefits')"
    />
  </section>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { useI18n } from "vue-i18n";

import { useUserStore } from "@/stores/user.store";
import { useChannelStore } from "@/stores/channel.store";
import { useMaybeWalletService } from "@/services/wallet/wallet.service";
import { useShareReferral } from "@/composables/use-share-referral";
import { formatCompactNumber } from "@/utils/format";

import ChannelProfileIdentityColumn from "./channel-profile-identity-column.vue";
import ChannelProfileXpColumn from "./channel-profile-xp-column.vue";
import ChannelProfileMembershipColumn from "./channel-profile-membership-column.vue";
import ChannelProfileSuperfanColumn from "./channel-profile-superfan-column.vue";

const props = withDefaults(
  defineProps<{
    /**
     * The "X/5 Quests Completed" string, computed by the parent (which owns the
     * loaded `contests` array). The card itself never fetches contests. The
     * `#quests-counter` slot overrides this when present.
     */
    completedLabel?: string;
    /**
     * When true the card renders WITHOUT its own panel chrome (no rounded
     * border / surface-secondary background / outer margin) so the host can
     * place it inside a shared panel — e.g. the unified hero panel in
     * channel.vue that groups the profile card and the MEANWHILE activity row
     * into ONE rounded light panel. Default false preserves the standalone
     * panel for any other usage.
     */
    bare?: boolean;
  }>(),
  { bare: false },
);

const emit = defineEmits<{
  /**
   * Single channel-wide upgrade intent. The host (channel.vue) forwards this to
   * the auth orchestrator, which resolves the available upgrade paths from
   * channel capabilities (membership and/or token) and routes to the chooser or
   * a direct flow. The card deliberately does NOT decide membership-vs-token.
   */
  (e: "upgrade"): void;
  /** Open the existing benefits surface (owned by the host). */
  (e: "see-benefits"): void;
}>();

const { t } = useI18n();
const userStore = useUserStore();
const channelStore = useChannelStore();
const walletService = useMaybeWalletService();

// Decorative invite-friends reward shown in the Total XP column's invite row,
// matching the design's "200XP" badge. There is no backend field for the
// referral reward today, so it is centralized here as a single named constant.
// TODO(follow-up): source referral reward from backend config.
const INVITE_FRIENDS_REWARD = "200";

// Superfan / VIP status is derived from existing channel-store getters — no new
// query. `isSuperfan` reflects the per-user superfan flag in this channel.
const isSuperfan = computed(() => channelStore.isSuperfan);

// Membership label: paid tier name (e.g. "VIP") > Superfan > Free Member.
//
// The MEMBERSHIP / WALLET column must show the user's actual paid membership
// TIER NAME, distinct from the SUPERFAN+ column (which shows ACTIVE). The tier
// name comes from the `userInChannel.membership.name` field already in the
// store (`userMembership`) — NOT a hardcoded "VIP" string.
//
// We must detect "has a paid tier" WITHOUT `channelStore.isVip`/`hasMembership`,
// because those derive from `Number(membership.price_tag) > 0` and `price_tag`
// is a FORMATTED money string (e.g. "€9.99 / mo") so `Number(price_tag)` is
// `NaN` → the check is always false and a real VIP member was mislabelled
// "Superfan". The reliable paid-vs-free signal is the tier's
// `permission_mask_level`: the resolver falls back to the channel's free tier
// (permission_mask_level 0) when the user has no membership purchase, so any
// level > 0 means a real paid tier. `isVip` is still honoured as a secondary
// signal for back-compat.
const hasPaidTier = computed(() => {
  const membership = channelStore.userMembership;
  return channelStore.isVip || (membership?.permission_mask_level ?? 0) > 0;
});

const membershipLabel = computed(() => {
  const membership = channelStore.userMembership;
  if (hasPaidTier.value && membership?.name) {
    return membership.name;
  }
  if (channelStore.isSuperfan) return t("channel.profileCard.superfan");
  return t("channel.profileCard.freeMember");
});

// XP / points — sourced from `channelStore.userPoints`.
const formattedXp = computed(() =>
  formatCompactNumber(channelStore.userPoints ?? 0, userStore.userLocale),
);

// Quests-progress track width — parses the "X/Y" completedLabel string into a
// percentage for the absolute brand-fill track behind the frosted label badge.
// Degrades gracefully to "0%" when the label is absent or not in "X/Y" form.
const questsTrackWidth = computed(() => {
  if (!props.completedLabel) return "0%";
  const match = /^(\d+)\s*\/\s*(\d+)$/.exec(props.completedLabel.trim());
  if (!match) return "0%";
  const completed = parseInt(match[1]!, 10);
  const total = parseInt(match[2]!, 10);
  if (total <= 0 || isNaN(completed) || isNaN(total)) return "0%";
  return `${Math.min(100, Math.round((completed / total) * 100))}%`;
});

// Channel default token for the WALLET line (token-supported channels only).
const walletToken = computed(
  () => walletService.value?.channelWebThreeTokens?.[0] ?? null,
);

const formattedTokenBalance = computed(() => {
  const token = walletToken.value;
  if (!token) return "0";
  const balance =
    walletService.value?.tokenBalanceMap.get(token.address)?.amount ?? 0;
  return formatCompactNumber(balance, userStore.userLocale);
});

const shareReferral = useShareReferral({
  openGraphObject: computed(() => ({
    title: channelStore.channel?.description ?? channelStore.channelSlug ?? "",
  })),
  referralObject: computed(() => ({
    title: `${channelStore.channel?.description} - FanFest`,
    text: `Join ${channelStore.channel?.description} on FanFest and earn rewards!`,
    payload: {
      community_id: channelStore.selectedCommunity ?? undefined,
    },
  })),
});

function onInviteFriends() {
  void shareReferral();
}

// Whether the channel OFFERS any upgrade path — membership (it sells a Superfan
// product) or token (it has a default token). This is a CHANNEL-offering check,
// not the user's own membership state, so a both-supported channel still shows
// the button and a channel that offers nothing hides it (the orchestrator would
// no-op anyway). The membership-vs-token routing is resolved downstream.
const hasAnyUpgradePath = computed(
  () => channelStore.hasSuperfanProduct || walletToken.value !== null,
);
</script>
